<template>
    <div class="weather-content">
        <div class="weather-content-scroll" ref="scroll">
            <template v-if="!weatherData && loc">
                <div class="weather-hero">
                    <div class="weather-hero-city">{{ cityName }}</div>
                    <div class="weather-hero-temp"><span class="weather-skel weather-skel--temp"></span></div>
                    <div class="weather-hero-condition"><span class="weather-skel weather-skel--cond"></span></div>
                    <div class="weather-hero-hilo"><span class="weather-skel weather-skel--hilo"></span></div>
                </div>
                <div class="weather-hourly-card">
                    <div class="weather-card-title">{{ $t('common.weather.hourly') }}</div>
                    <div class="weather-hourly-strip">
                        <div v-for="j in 8" :key="j" class="weather-hourly-item">
                            <span class="weather-skel weather-skel--htim"></span>
                            <span class="weather-skel weather-skel--hico"></span>
                            <span class="weather-skel weather-skel--htmp"></span>
                        </div>
                    </div>
                </div>
                <div class="weather-bottom-row">
                    <div class="weather-daily-card">
                        <div class="weather-card-title">{{ $t('common.weather.daily') }}</div>
                        <div class="weather-daily-list">
                            <div v-for="j in 5" :key="j" class="weather-daily-row">
                                <span class="weather-skel weather-skel--dday"></span>
                                <span class="weather-skel weather-skel--dico"></span>
                                <div class="weather-daily-bar-wrap"><span class="weather-skel weather-skel--dbar"></span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
            <div class="weather-hero">
                <div class="weather-hero-city">{{ cityName }}</div>
                <div class="weather-hero-temp">{{ temperature }}°</div>
                <div class="weather-hero-condition">{{ conditionLabel }}</div>
                <div class="weather-hero-hilo" v-if="todayDaily">
                    {{ $t('common.weather.high') }}:{{ Math.round(todayDaily.temp_max) }}°
                    {{ $t('common.weather.low') }}:{{ Math.round(todayDaily.temp_min) }}°
                </div>
            </div>

            <div class="weather-hourly-card" v-if="hourly.length">
                <div class="weather-card-title">{{ $t('common.weather.hourly') }}</div>
                <div class="weather-hourly-strip" ref="hourlyStrip">
                    <div v-for="(h, i) in visibleHourly" :key="i" class="weather-hourly-item"
                         :class="{'weather-hourly-item--sunrise': h.type === 'sunrise', 'weather-hourly-item--sunset': h.type === 'sunset'}">
                        <span class="weather-hourly-time">{{ h.timeLabel }}</span>
                        <span class="weather-hourly-icon" v-if="h.type === 'hour'">{{ getConditionEmoji(h.condition) }}</span>
                        <span class="weather-hourly-icon" v-else-if="h.type === 'sunrise'">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4"/><path d="M4.93 10.93l2.83 2.83"/><path d="M2 18h2"/><path d="M20 18h2"/><path d="M16.24 13.76l2.83-2.83"/><path d="M18 22H6"/><path d="M12 18a6 6 0 0 0 0-12"/></svg>
                        </span>
                        <span class="weather-hourly-icon" v-else-if="h.type === 'sunset'">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 10v4"/><path d="M4.93 10.93l2.83 2.83"/><path d="M2 18h2"/><path d="M20 18h2"/><path d="M16.24 13.76l2.83-2.83"/><path d="M18 22H6"/><path d="M12 18a6 6 0 0 0 0-12"/></svg>
                        </span>
                        <span class="weather-hourly-temp" v-if="h.type === 'hour'">{{ Math.round(h.temperature) }}°</span>
                        <span class="weather-hourly-temp" v-else>{{ h.timeLabel2 || '' }}</span>
                    </div>
                </div>
            </div>

            <div class="weather-bottom-row">
                <div class="weather-daily-card" v-if="daily.length">
                    <div class="weather-card-title">{{ $t('common.weather.daily') }}</div>
                    <div class="weather-daily-list">
                        <div v-for="(d, i) in daily" :key="i" class="weather-daily-row">
                            <span class="weather-daily-day">{{ i === 0 ? $t('common.weather.today') : getDayName(d.date) }}</span>
                            <span class="weather-daily-icon">{{ getConditionEmoji(d.condition) }}</span>
                            <span class="weather-daily-precip" v-if="d.precipitation_probability != null">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>
                                {{ d.precipitation_probability }}%
                            </span>
                            <span class="weather-daily-min">{{ Math.round(d.temp_min) }}°</span>
                            <div class="weather-daily-bar-wrap">
                                <div class="weather-daily-bar" :style="barStyle(d)"></div>
                            </div>
                            <span class="weather-daily-max">{{ Math.round(d.temp_max) }}°</span>
                        </div>
                    </div>
                </div>

                <div class="weather-details-card" v-if="current">
                    <div class="weather-card-title">{{ $t('common.weather.details') }}</div>
                    <div class="weather-details-grid">
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.feelsLike') }}</span>
                            <span class="weather-detail-value">{{ Math.round(current.feels_like) }}°</span>
                        </div>
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.humidity') }}</span>
                            <span class="weather-detail-value">%{{ current.humidity }}</span>
                        </div>
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.wind') }}</span>
                            <span class="weather-detail-value">{{ current.wind_speed }} km/h</span>
                        </div>
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.uvIndex') }}</span>
                            <span class="weather-detail-value">{{ current.uv_index }}</span>
                        </div>
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.pressure') }}</span>
                            <span class="weather-detail-value">{{ current.pressure }} hPa</span>
                        </div>
                        <div class="weather-detail-item">
                            <span class="weather-detail-label">{{ $t('common.weather.visibility') }}</span>
                            <span class="weather-detail-value">{{ current.visibility }} km</span>
                        </div>
                        <div class="weather-detail-item weather-detail-item--sun" v-if="todayDaily?.sunrise">
                            <span class="weather-detail-label">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                                {{ todayDaily.sunrise }}
                            </span>
                            <span class="weather-detail-label">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                                {{ todayDaily.sunset }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            </template>
        </div>
    </div>
</template>

<script>
import weatherService from './weatherService'

const CONDITION_EMOJIS = {
    0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️',
    45: '☁️', 48: '☁️',
    51: '🌦️', 53: '🌦️', 55: '🌧️',
    56: '🌧️', 57: '🌧️',
    61: '🌦️', 63: '🌧️', 65: '🌧️',
    66: '🌧️', 67: '🌧️',
    71: '🌨️', 73: '🌨️', 75: '❄️',
    77: '❄️',
    80: '🌦️', 81: '🌧️', 82: '🌧️',
    85: '🌨️', 86: '❄️',
    95: '⛈️', 96: '⛈️', 99: '⛈️',
    clear: '☀️', mainly_clear: '🌤️', partly_cloudy: '⛅', overcast: '☁️',
    fog: '☁️', drizzle: '🌦️', freezing_drizzle: '🌧️',
    rain: '🌧️', freezing_rain: '🌧️', rain_shower: '🌦️',
    snow: '🌨️', snow_grains: '❄️', snow_shower: '🌨️',
    thunderstorm: '⛈️', night_clear: '🌙',
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export default {
    name: 'WeatherContent',
    computed: {
        loc() {
            return weatherService.activeLocation.value
        },
        weatherData() {
            return this.loc?.data || null
        },
        current() {
            return this.weatherData?.current || null
        },
        hourly() {
            return this.weatherData?.hourly || []
        },
        daily() {
            return this.weatherData?.daily || []
        },
        todayDaily() {
            return this.daily[0] || null
        },
        cityName() {
            if (!this.loc) return ''
            if (this.loc.districtName && this.loc.cityName && this.loc.districtName !== this.loc.cityName) {
                return this.loc.cityName + ', ' + this.loc.districtName
            }
            return this.loc.cityName || this.loc.districtName || ''
        },
        temperature() {
            return this.current?.temperature != null ? Math.round(this.current.temperature) : '--'
        },
        conditionLabel() {
            return this.current?.condition?.label || ''
        },
        tempRange() {
            let min = Infinity, max = -Infinity
            for (const d of this.daily) {
                if (d.temp_min < min) min = d.temp_min
                if (d.temp_max > max) max = d.temp_max
            }
            return { min, max }
        },
        visibleHourly() {
            const items = []
            const sunrise = this.todayDaily?.sunrise
            const sunset = this.todayDaily?.sunset
            let sunriseInserted = false
            let sunsetInserted = false

            for (let i = 0; i < Math.min(24, this.hourly.length); i++) {
                const h = this.hourly[i]
                const time = h.time?.slice(11, 16) || ''

                if (sunrise && !sunriseInserted && time >= sunrise) {
                    items.push({ type: 'sunrise', timeLabel: this.$t('common.weather.sunriseShort'), timeLabel2: sunrise })
                    sunriseInserted = true
                }
                if (sunset && !sunsetInserted && time >= sunset) {
                    items.push({ type: 'sunset', timeLabel: this.$t('common.weather.sunsetShort'), timeLabel2: sunset })
                    sunsetInserted = true
                }

                items.push({
                    type: 'hour',
                    timeLabel: i === 0 ? this.$t('common.weather.now') : time,
                    temperature: h.temperature,
                    condition: h.condition,
                })
            }
            return items
        },
    },
    methods: {
        getConditionEmoji(cond) {
            if (!cond) return '🌤️'
            if (typeof cond !== 'object') return CONDITION_EMOJIS[cond] || '🌤️'
            return CONDITION_EMOJIS[cond.id] || CONDITION_EMOJIS[cond.icon] || '🌤️'
        },
        getDayName(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            const dayIdx = d.getDay()
            return this.$t('common.weather.days.' + DAY_KEYS[dayIdx])
        },
        barStyle(d) {
            const range = this.tempRange
            const total = range.max - range.min || 1
            const left = ((d.temp_min - range.min) / total) * 100
            const width = ((d.temp_max - d.temp_min) / total) * 100
            return {
                marginLeft: left + '%',
                width: Math.max(width, 8) + '%',
            }
        },
    },
}
</script>
